import React from 'react'
import { useTranslation } from 'react-i18next'
import FieldItem from './FieldItem'
import { useNavigate } from 'react-router-dom'
import { URLS } from '../../../_config'
import { trackEvent } from '../../../_common/functions'

interface TemplateField {
    label: string
    value: string
}

interface TemplateCardProps {
    template: any
    name: string
    lastUpdated: string
    owner: string
    topFields: TemplateField[]
    bottomFields: TemplateField[]
    source?: 'search' | 'detail'
    domainId?: string
}

const TemplateCard: React.FC<TemplateCardProps> = ({ template, name, lastUpdated, owner, topFields, bottomFields, source = 'search', domainId }) => {
    const { t } = useTranslation()
    const navigate: any = useNavigate()
    const isTemplateSelected: boolean = !template.can_apply
    const chooseTemplate = () => {
        trackEvent('template_use_template_clicked', {
            template_literal: template.id,
            template_name: name,
            source,
            ...(domainId ? { domain_id: domainId } : {}),
        })
        const templateData: any = {
            name,
            data: template,
        }
        sessionStorage.setItem('SELECTED_TEMPLATE', JSON.stringify(templateData))
        navigate(URLS.CREATE_EXPERIMENT('new'))
    }
    return (
        <div className="bg-black rounded-[0.625rem] w-full">
            <div className="flex flex-col gap-[0.833rem] items-start px-[0.625rem] py-[0.833rem] w-full">
                {/* Header */}
                <div className="flex items-center justify-between w-full">
                    <p className="font-['Poppins',sans-serif] font-bold leading-normal text-[0.667rem] text-white">{name}</p>
                    <p className="font-['Poppins',sans-serif] leading-normal text-[0.542rem] text-white">
                        <span className="font-normal">{t('Last updated')} </span>
                        <span className="font-semibold">{lastUpdated}</span>
                        <span className="font-medium"> ; </span>
                        <span className="font-normal">{t('Owner')} : </span>
                        <span className="font-semibold">{owner}</span>
                    </p>
                </div>

                {/* Content area */}
                <div className="bg-[#d6d6d6] rounded-[0.417rem] w-full">
                    <div className="flex flex-col gap-[0.667rem] px-[0.625rem] py-[0.417rem] w-full">
                        {/* Top row - yellow dots */}
                        <div className="bg-white rounded-[0.5rem] w-full">
                            <div className="border border-[#f4f4f4] rounded-[0.5rem]">
                                <div className="flex gap-[0.833rem] items-start p-[0.417rem] w-full">
                                    {topFields.map((field) => (
                                        <FieldItem key={field.label} label={field.label} value={field.value} dotColor="yellow" />
                                    ))}
                                </div>
                            </div>
                        </div>

                        {/* Bottom row - white dots */}
                        <div className="flex gap-[0.667rem] items-start w-full">
                            {bottomFields.map((field) => (
                                <FieldItem key={field.label} label={field.label} value={field.value} dotColor="white" />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Use This Template button */}
                <div className="flex items-center justify-center w-full">
                    <button
                        type="button"
                        disabled={isTemplateSelected}
                        onClick={chooseTemplate}
                        className={`bg-[#f5e003] flex gap-[0.208rem] items-center justify-center px-[0.333rem] py-[0.208rem] rounded-[0.208rem] cursor-pointer ${isTemplateSelected ? 'opacity-50' : ''}`}
                    >
                        <div className="h-[0.375rem] relative shrink-0 w-[0.542rem]">
                            <svg className="absolute block inset-0" fill="none" preserveAspectRatio="none" viewBox="0 0 13 9" aria-hidden="true">
                                <path
                                    d="M12.8476 0.853965L4.52799 8.85337C4.4797 8.89985 4.42235 8.93673 4.35922 8.96189C4.2961 8.98705 4.22844 9 4.1601 9C4.09177 9 4.02411 8.98705 3.96098 8.96189C3.89786 8.93673 3.84051 8.89985 3.79222 8.85337L0.152382 5.35363C0.0548135 5.25982 0 5.13258 0 4.99991C0 4.86723 0.0548135 4.73999 0.152382 4.64618C0.249951 4.55237 0.382283 4.49966 0.520266 4.49966C0.658249 4.49966 0.790581 4.55237 0.88815 4.64618L4.1601 7.79282L12.1119 0.146517C12.2094 0.0527035 12.3418 -9.88484e-10 12.4797 0C12.6177 9.88484e-10 12.75 0.0527035 12.8476 0.146517C12.9452 0.24033 13 0.367569 13 0.500241C13 0.632913 12.9452 0.760151 12.8476 0.853965Z"
                                    fill="black"
                                />
                            </svg>
                        </div>
                        <p className="font-['Poppins',sans-serif] font-medium leading-normal text-[0.625rem] text-black">{t('Use This Template')}</p>
                    </button>
                </div>

                {isTemplateSelected && (
                    <div className="bg-[#fdf598] content-stretch flex items-center justify-center p-[0.208rem] relative rounded-[0.5rem] size-full">
                        <p className="flex-[1_0_0] font-['Poppins',sans-serif] leading-[0] min-h-px min-w-px not-italic relative text-[#896146] text-[13px] text-center whitespace-pre-wrap">
                            <span className="font-normal leading-[normal]">{t('You are not allowed to use this template')}</span>
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}
export default TemplateCard
